<template>
  <fts-modal
    :append-to-body="appendToBody"
    :width="width"
    :full-screen="fullScreen"
    :escape-event="escapeEvent"
    :backdrop="backdrop"
    :persistent="persistent"
    :header="isHeader"
    :footer="isFooter"
    :append-container-class="appendContainerClass"
    :header-class="headerClass"
    :content-class="contentClass"
    :footer-class="footerClass"
    class="fts-design-modal"
    ref="ftsDesignModal"
    @opened="opened"
    @close="beforeClose"
    @focus="focus"
    @blur="blur"
    v-if="showDialog">
    <!-- header -->
    <template #header>
      <!-- header wrapper -->
      <slot name="header">
        <div class="design-modal-header">
          <!-- title -->
          <slot name="title" v-if="isTitle">
            <div class="design-modal-header-title">{{title}}</div>
          </slot>
          <!-- description -->
          <slot name="description" v-if="isDescription">
            <div class="design-modal-header-description">{{desc}}</div>
          </slot>
        </div>
        <!-- actions -->
        <slot name="header-actions" actions-class="design-modal-header-actions" action-class="design-modal-header-action">
          <div class="design-modal-header-actions">
            <slot name="actions" action-class="design-modal-header-action" />
            <fts-btn
              text
              icon
              class="design-modal-header-action"
              :disabled="disabledClose"
              @click="beforeClose"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </fts-btn>
          </div>
        </slot>
      </slot>
    </template>
    <!-- body -->
    <template #body>
      <slot name="body" />
    </template>
    <!-- footer -->
    <template #footer>
      <slot name="footer" :beforeClose="beforeClose" :close="close">
        <div class="design-modal-footer-l" v-if="isFooterLeft">
          <slot name="footerLeft" :beforeClose="beforeClose" :close="close"></slot>
        </div>
        <div class="design-modal-footer-r" v-if="isFooterRight">
          <slot name="footerRight" :beforeClose="beforeClose" :close="close"></slot>
        </div>
      </slot>
    </template>
  </fts-modal>
</template>

<script>
import FTSModal from "../elements/FTSModal";
import FTSBtn from "../elements/FTSBtn";

export default {
  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
  },
  props: {
    appendToBody: Boolean,
    width: String,
    fullScreen: Boolean,
    backdrop: {
      type: Boolean,
      default: true,
    },
    escapeEvent: Boolean,
    persistent: Boolean,
    appendContainerClass: String,
    appendHeaderClass: String,
    appendContentClass: String,
    appendFooterClass: String,
    title: String,
    desc: String, // legacy, to-do change to description
    description: String,
    disabledClose: Boolean,
  },
  data: () => ({
    showDialog: false,
    beforeCloseHandler: null,
  }),
  methods: {
    show(item) {
      if (item && item.beforeCloseHandler)
        this.beforeCloseHandler = item.beforeCloseHandler;
  
      this.showDialog = true;
    },
    opened() {
      this.$emit("opened");
    },
    beforeClose() {
      if (this.disabledClose) return;

      if (this.beforeCloseHandler && typeof this.beforeCloseHandler === 'function') {
        this.beforeCloseHandler();
      } else {
        this.close();
      }
    },
    close() {
      this.showDialog = false;
      this.$emit("closed");
    },
    focus() {
      this.$emit('focus');
    },
    blur() {
      this.$emit('blur');
    },
    resetScroll() {
      this.$refs.ftsDesignModal.resetScroll();
    }
  },
  computed: {
    isTitle() {
      return (
        Boolean(this.title) ||
        Boolean(this.$slots.title) ||
        Boolean(this.$scopedSlots.title)
      );
    },
    isDescription() {
      return (
        Boolean(this.desc) ||
        Boolean(this.$slots.description) ||
        Boolean(this.$scopedSlots.description)
      );
    },
    isHeader() {
      return (
        this.isTitle ||
        this.isDescription ||
        Boolean(this.$slots.header) ||
        Boolean(this.$scopedSlots.header)
      );
    },
    isFooterLeft() {
      return Boolean(this.$slots.footerLeft) || Boolean(this.$scopedSlots.footerLeft);
    },
    isFooterRight() {
      return Boolean(this.$slots.footerRight) || Boolean(this.$scopedSlots.footerRight);
    },
    isFooter() {
      return (
        this.isFooterLeft ||
        this.isFooterRight ||
        Boolean(this.$slots.footer) ||
        Boolean(this.$scopedSlots.footer)
      );
    },
    headerClass() {
      let classes = "design-modal__header";
      if (this.appendHeaderClass)
        classes += ` ${this.appendHeaderClass}`

      return classes
    } , 
    contentClass() {
      let classes = "design-modal__body";
      if (this.appendContentClass)
        classes += ` ${this.appendContentClass}`

      return classes
    },
    footerClass() {
      let classes = "design-modal__footer";
      if (this.isFooterLeft || this.isFooterRight)
        classes += " row"
      if (this.appendFooterClass)
        classes += ` ${this.appendFooterClass}`
      
      return classes
    },
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-design-modal {
  .design-modal__header {
    display: flex;
    align-items: start;
    padding: 1rem 1.5rem;
    .design-modal-header {
      min-width: 0;
      flex: 1;
    }
    .design-modal-header-title {
      @include text-overflow-hidden;
      font-weight: 500;
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    .design-modal-header-tag {
      margin-right: 0.125rem;
      padding: 0.125rem 0.25rem;
      border-radius: 0.25rem;
      background: var(--fts-primary);
      color: var(--fts-light);
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
    }
    .design-modal-header-description {
      color: var(--fts-default);   
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
    .design-modal-header-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin: -0.25rem -0.135rem -0.135rem;
      padding-left: 1rem;
      .design-modal-header-action {
        margin: 0.135rem;
      }
    }
    // studio use
    .design-modal-header-std__title {
      min-width: 0;
      display: flex;
      align-items: center;
      &.with-hint {
        position: relative;
        .design-modal-header-hint {
          @include user-select-none;
          @include theme {
            background-color: rgba(theme-get('fts-popover-alt-background'), 0.85);
          }
          position: absolute;
          right: 0;
          bottom: 0.125rem;
          transform: translateY(100%);
          padding: 0.05rem 0.5rem;
          border-radius: 0.5rem;
          box-shadow: var(--fts-elevation-2);
          color: var(--fts-text);
          font-size: 0.75rem;
          line-height: 1rem;
          pointer-events: none;
        }
      }
      .design-modal-header-tag {  
        display: inline-flex;
        margin-right: .5rem;
        > div {
          padding: .125rem .5rem;
          border-radius: .25rem;
          background-color: var(--fts-primary);
          color: var(--fts-light);
          font-weight: 500;
          font-size: 1.125rem;
          line-height: 1.5rem;
          letter-spacing: 0.05em;
          user-select: none;
        }
        cursor: default;
        &::after {
          content: "";
          margin-left: .5rem;
          border-right: 1px solid var(--fts-border);
        }
      }
      .design-modal-header-emoji {
        & + .design-modal-header-name {
          margin-left: .75rem;
        }
        & + .design-modal-header-input {
          margin-left: calc(0.5rem - 0.375rem);
        }
      }
      .design-modal-header-icon {
        font-size: 1.125rem;
        line-height: 1.75rem;
        &.default {
          color: var(--fts-default);
        }  
        & + .design-modal-header-name {
          margin-left: 0.5rem;
        }
        & + .design-modal-header-input {
          margin-left: calc(0.5rem - 0.375rem);
        }
      }
      @mixin design-modal-header-name {
        font-weight: 500;
        font-size: 1.125rem;
        line-height: 1.75rem;
      }
      .design-modal-header-name {
        @include design-modal-header-name;
        @include text-overflow-ellipsis;
        &.design-modal-header-name--flex-1 {
          flex: 1;
        }
      }
      .design-modal-header-input {
        @include design-modal-header-name;
        height: auto;
        min-height: auto;
        padding: 0 .375rem;
        border: none;
        border-radius: .25rem;
        box-shadow: none;
        &::-webkit-contacts-auto-fill-button,
        &::-webkit-credentials-auto-fill-button {
          visibility: hidden !important;
          display: none !important;
          pointer-events: none !important;
          height: 0 !important;
          width: 0 !important;
          margin: 0 !important;
        }
        &.error {
          @include theme() {
            background: rgba($color: theme-get("fts-danger"), $alpha: 0.1);
          }
        }
      }
    }
  }
  .design-modal__body {
    padding: 0 1.5rem 1.5rem;
    &.p-none {
      padding: 0;
    }
    .design-modal-body-description {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-default);
    }
  }
  .design-modal__footer {
    &.row {
      display: flex;
    }
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--fts-border);
    text-align: right;
    .design-modal-footer-l {
      @include flex-gap($gap: .375rem, $width: auto);
    }
    .design-modal-footer-r {
      @include flex-gap($gap: .375rem, $width: auto);
      justify-content: flex-end;
      flex: 1;
    }
  }
  /* spinner */
  .design-modal-spinner {
    width: 100%;
    padding: 2rem 0;
  }
  /* form */
  .design-modal-form-group {
    margin-bottom: 1.5rem;
  }
  .design-modal-form-legend {
    margin-bottom: .75rem;
    font-size: 1.15rem;
    font-weight: 500;
    line-height: 1.5;
    letter-spacing: .0892857143em;
    color: $grayColor;
  }
  .design-modal-form-label {
    margin-bottom: .375rem;
    font-size: .875rem;
    color: var(--fts-form-label);
  }
  .design-modal-form-helper {
    margin-bottom: .375rem;
    font-size: 0.75rem;
    color: var(--fts-form-helper);
    line-height: 1rem;
  }
  .design-modal-form-error {
    margin-top: .125rem;
    margin-bottom: .375rem;
    font-size: .75rem;
    line-height: 1rem;
    color: var(--fts-danger);
  }
  .design-modal-form-display {
    min-height: $comfort-form-control-height; 
    line-height: $comfort-line-height;
  }
  /* content header */
  .design-modal-content__header {
    margin-bottom: 2rem;
    text-align: center;
    .design-modal-content__header-title {
      font-weight: 700;
      font-size: 1.5rem;
      line-height: 2rem;
      color: var(--fts-primary);
    }
    .design-modal-content__header-title + .design-modal-content__header-description {
      margin-top: .25rem;
    }
    .design-modal-content__header-description {
      font-size: 1rem;
      line-height: 1.5rem;
      color: var(--fts-text);
    }
  }
  /* advanced toggle */
  .design-modal-advanced-toggle {
    margin-left: -0.75rem; 
    &.open {
      .design-modal-advanced-toggle-icon {
        transform: rotate(90deg);
      }
    }
    .design-modal-advanced-toggle-icon {
      margin-left: .75rem;
      margin-right: 0;
      transition: transform .3s;
    }
    .design-modal-advanced-toggle-error {
      height: 0.375rem;
      width: 0.375rem;
      margin-right: 0.575rem;
      border-radius: 10em;
      background: var(--fts-danger);
    }
  }
  .design-modal-advanced-content {
    margin-top: 1rem;
  }
  /* toggle swtich */
  .design-modal-toggle-switch {
    .design-modal-toggle-switch__label {
      margin-left: 0.375rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--fts-form-label);
    }
  }
  /* label group */
  .design-modal-label-group {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
    .design-modal-label-group-l {
      flex: 1;
      word-break: break-word;
      .fts-form-label {
        margin-bottom: 0;
        font-size: .875rem;
        & + .fts-form-helper {
          margin-top: .375rem;
        }
      }
      .fts-form-helper {
        margin-bottom: 0;
      }
    }
    .design-modal-label-group-r {
      margin-left: .75rem;
    }
  }
  /* card */
  .design-modal-card {
    padding: 1rem 1rem 0;
    box-shadow: var(--fts-elevation-2);
    border-radius: 0.25rem;
    background: var(--fts-card-background);
    &.design-modal-card-mt {
      margin-top: .75rem;
    }
    &.design-modal-card-pb {
      padding-bottom: 1rem;
    }
  }
  /* swatches */
  .design-modal-header-swatches {
    display: inline-flex;
    margin-left: .75rem;
    /* override */
    &.vue-swatches {
      .vue-swatches__trigger {
        height: 1.25rem !important;
        width: 1.25rem !important; 
      }
      .vue-swatches__container {
        position: absolute;
        right: 0px;
      }
    }
  }
  /* collapsible */
  .dm-collapsible {
    margin-bottom: 2rem;
    box-shadow: var(--fts-elevation-2);
    background: var(--fts-card-background);
    border-radius: 0.25rem;
    .dm-collapsible-header {
      padding: 1rem;
      display: flex;
      align-items: flex-start;
      .dm-collapsible-header-wrapper {
        flex: 1;
      }
      .dm-collapsible-header__title {
        display: flex;
        align-items: center;
        margin-bottom: .125rem;;
      }
      .dm-collapsible-header-title {
        font-size: 1.5rem;
        font-weight: 500;
        line-height: 2rem;
        color: var(--fts-form-label);
      }
      .dm-collapsible-header-description {
        margin-bottom: .75rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-form-helper);
      }
      .dm-collapsible-header-error {
        margin-bottom: .375rem;
        font-size: .75rem;
        line-height: 1rem;
        color: var(--fts-danger);
      }
      .dm-collapsible-header-tag {
        display: flex;
        align-items: center;
        margin-left: .5rem;
        padding: .175rem .575rem;
        border-radius: 10em;
      }
      .dm-collapsible-header-tag-action {
        @extend .dm-collapsible-header-tag;
        border: 1px solid var(--fts-primary);
        color: var(--fts-primary);
        cursor: pointer;
        .dm-collapsible-header-tag-label {
          white-space: nowrap;
          font-size: .75rem;
        }
        .dm-collapsible-header-tag-icon {
          margin-right: .375rem;
          font-size: .625rem;
          line-height: .125rem;
        }
      }
      .dm-collapsible-header-tag-form {
        @extend .dm-collapsible-header-tag;
        @include theme() {
          background-color: rgba(theme-get('fts-primary'), .125);
        }
        .tag-form-icon {
          margin-right: .75rem;
          font-size: 1rem;
          color: var(--fts-primary);
        }
        .tag-form-action {
          @include theme() {
            color: rgba(theme-get('fts-primary'), .65);
          }
          font-size: .75rem;
          cursor: pointer;
        }
      }
      .dm-collapsible-header-actions {
        display: flex;
        align-items: center;
        .dm-collapsible-control {
          &.dm-collapsible-control--active {
            transform: rotate(90deg);
          }
          color: var(--fts-default);
          font-size: 1.25rem;
          transition: transform .3s;
          user-select: none;
          cursor: pointer;
        }
      }
    }
    .dm-collapsible-content {
      padding-bottom: 1rem;
      padding-right: 1rem;
      padding-left: 1rem;
    }
    .dm-collapsible-footer {
      padding-left: 1rem;
      padding-right: 1rem;
      padding-bottom: 1rem;
      text-align: right;
    }
  }
  /* stepper */
  .dm-stepper {
    display: flex;
    flex-direction: column;
    padding: 0;
    .dm-stepper-header {
      padding: 1rem 1.5rem;
      border-top: 1px solid var(--fts-border);
      .dm-stepper-header-outer {
        @include flex-gap($gap: 0.5rem);
        align-items: center;
        justify-content: center;
        flex-wrap :wrap;
      }
    }
    .dm-stepper-footer {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
      padding-bottom: 1rem;
      .dm-stepper-footer-outer {
        @include flex-gap($gap: 0.5rem);
        justify-content: flex-end;
      }
    }
    .dm-stepper-tab {
      min-width: 0;
      display: flex;
      align-items: center;
      padding: 0.25rem 0.5rem;
      border-radius: 0.5rem;
      cursor: pointer;
      &:hover {
        .dm-stepper-tab-label {
          color: var(--fts-primary);
        }
        &:not(.dm-stepper-tab--select) {
          .dm-stepper-tab-icon {
            @include theme {
              background-color: rgba(theme-get('fts-primary'), 0.125);
            }
            color: var(--fts-primary);
          }
        }
      }
      &.dm-stepper-tab--select {
        @include theme {
          background-color: rgba(theme-get('fts-primary'), 0.125);
        }
        .dm-stepper-tab-label {
          color: var(--fts-primary);
        }
        &:not(.dm-stepper-tab--active) {
          .dm-stepper-tab-icon {
            background-color: var(--fts-primary);
          }
        }
      }
      &.dm-stepper-tab--active {
        .dm-stepper-tab-icon {
          background-color: var(--fts-primary);
        }
      }
      .dm-stepper-tab-icon {
        @include flex-center-middle;
        flex-shrink: 0;
        height: 2.25rem;
        width: 2.25rem;
        margin-right: 0.5rem;
        border-radius: 10em;
        background-color: var(--fts-default);
        color: var(--fts-light);
        font-size: 1.25rem;
        &.dm-stepper-tab-icon--fa {
          font-size: 1rem;
        }
      }
      .dm-stepper-tab-label {
        white-space: nowrap;
        font-size: 1.125rem;
        line-height: 1.75rem;
      }
    }
    .dm-stepper-tab-line {
      @include flex-center-middle;
      height: 2.5rem;
      width: 10%;
      min-width: 50px;
      &::before {
        content: '';
        width: 100%;
        border-top: 1px solid var(--fts-border);
      }
    }
    .dm-stepper-content {
      flex: 1 1 100%;
      padding: 0.5rem 1rem 1rem;
      overflow: auto;
    }
    .dm-stepper-control {
      margin-bottom: 1rem;
      .dm-stepper-control-outer {
        @include flex-gap($gap: 0.5rem);
      }
    }
    .dm-stepper-description {
      margin-bottom: 0.75rem;
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--fts-default);
    }
    .dm-stepper-action {
      display: flex;
      align-items: center;
      margin-left: 0.5rem;
      padding: 0.175rem 0.575rem;
      border: 1px solid var(--fts-default);
      border-radius: 10em;
      color: var(--fts-default);
      font-size: 0.75rem;
      cursor: pointer;
    }
    .dm-stepper-message {
      @include theme {
        background-color: rgba(theme-get('fts-default'), 0.125);
      }
      padding: 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--fts-default);
      &.dm-stepper-message--error {
        @include theme {
          background-color: rgba(theme-get('fts-danger'), 0.125);
        }
        color: var(--fts-danger);
      }
    }
  }
  /* flow section */
  .dm-flow-section {
    margin-bottom: 2rem;
    .dm-flow-section-title {
      margin-bottom: .375rem;
      font-size: .75rem;
      line-height: 1rem;
      color: var(--fts-form-helper);
    }
  }
  /* flow display */
  .dm-flow-display {
    &:not(:last-child) {
      margin-bottom: 1.25rem;
    }
    .dm-flow-wrapper {
      @include flex-gap($display: flex, $gapTop: .375rem, $gapLeft: .75rem, $width: auto);
      min-width: 0;
      flex: 1;
      align-items: center;
      padding-right: 5.5rem; /* for FTSStateDesignInline action:activity */
    }
    .dm-flow-action {
      margin-left: 1rem;
    }
    .dm-flow-display-direction {
      color: var(--fts-default);
    }
    .dm-flow-display-error {
      flex: 1 0 100%;
      font-size: .75rem;
      color: var(--fts-danger);
    }
  }
  /* flow state start */
  .dm-flow-state-start {
    @include flex-center-middle;
    height: 32px;
    width: 32px;
    border-radius: 10em;
    background-color: #73cae0;
    color: var(--fts-light);
    font-size: .75rem;
  }
  /* form design create */
  .dm-form-design-create {
    @include theme {
      background-color: rgba(theme-get('fts-primary'), 0.125);
    }
    position: relative;
    height: 6.5rem;
    width: 6.5rem;
    margin: 1.5rem auto;
    border-radius: 10em;
    cursor: pointer;
    transition: all .3s ease-in-out;
    .dm-form-design-create-icon {
      @include absolute-center-middle;
      font-size: 3.5rem;
      color: var(--fts-primary);
      transition: inherit;
    }
    .dm-form-design-create-plus {
      @include flex-center-middle;
      @include absolute-center-y;
      right: -1rem;
      height: 2.25rem;
      width: 2.25rem;
      border-radius: 10em;
      background-color: var(--fts-secondary);
      color: var(--fts-light);
      transition: inherit;
    }
    &:hover {
      background-color: var(--fts-primary);
      .dm-form-design-create-icon {
        color: var(--fts-light);
      }
      .dm-form-design-create-plus {
        right: -1.125rem;
      }
    }
  }
  /* unavailable message */
  .dm-unavailable-message {
    padding: 0.75rem;
    border-radius: 0.25rem;
    background-color: var(--fts-gray-100);
    font-size: 0.875rem;
    line-height: 1.5;
    &.dm-unavailable-message--warning {
      background-color: rgba(var(--fts-warning-rgb), 0.1);
      color: var(--fts-warning);
    }
  }
}
/* fts select */
.fts-select__list-header {
  margin: .375rem 1rem .575rem;
  padding-bottom: .575rem;
  border-bottom: 1px solid var(--fts-border);
}
.fts-select__no-options-footer {
  width: 100%;
  margin-top: .75rem;
}
</style>